import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { useSelector } from 'react-redux'
import Dropdown, {
  divider,
} from '@groovehq/internal-design-system/lib/components/Dropdown/Dropdown'
import Checkbox from '@groovehq/internal-design-system/lib/components/Checkbox/Checkbox'
import Avatar from '@groovehq/internal-design-system/lib/components/Avatar/Avatar'
import { text } from '@groovehq/internal-design-system/lib/styles/elements'
import { emptyArr } from 'util/arrays'
import { selectAgentsSortedByName } from 'selectors/agents/base'
import { Footer, styles, useMultiSelect } from '../MultiSelectMenu'
import MenuWithSearch, {
  useShowHeaderShadow,
  styles as menuWithSearchStyles,
} from '../MenuWithSearch'

const TeammatesDropdown = ({
  value: inputValue,
  setValue,
  selectedIds,
  onSelectedIdsChange,
}) => {
  const searchRef = useRef()
  const value = inputValue || emptyArr
  const agents = useSelector(selectAgentsSortedByName)
  const [menuVisible, setMenuVisible] = useState(false)
  const [filteredList, setFilteredList] = useState([])
  const [search, setSearch] = useState('')
  const {
    indeterminate,
    allChecked,
    handleCheckAllChange,
    handleChange,
  } = useMultiSelect(filteredList, onSelectedIdsChange, selectedIds)
  const { handleScroll, shouldShowHeaderShadow } = useShowHeaderShadow(
    menuVisible
  )

  const handleMenuVisibleChange = useCallback(visible => {
    setMenuVisible(visible)
  }, [])
  const handleSelect = useCallback(
    () => {
      setValue(selectedIds)
      setMenuVisible(false)
    },
    [setValue, selectedIds]
  )
  const handleCancel = useCallback(() => {
    setMenuVisible(false)
  }, [])
  const handleChangeSearch = useCallback(
    ({ target }) => {
      setSearch(target.value)
      if (target.value?.trim()) {
        const newList = agents.filter(({ name }) =>
          name.toLowerCase().includes(target.value?.trim().toLowerCase())
        )
        setFilteredList(newList)
        onSelectedIdsChange(
          selectedIds.filter(id => newList.some(item => item.id === id))
        )
      } else {
        setFilteredList(agents)
      }
    },
    [agents, onSelectedIdsChange, selectedIds]
  )

  const menu = useMemo(
    () => {
      return (
        <MenuWithSearch
          focusElementRef={searchRef}
          search={search}
          onScroll={handleScroll}
        >
          {!!filteredList.length && (
            <Checkbox
              id="all-teammates"
              indeterminate={indeterminate}
              checked={allChecked}
              css={text.styles.fontMedium}
              gap={16}
              className="grui px-10 pb-5"
              onChange={handleCheckAllChange}
            >
              All {app.t('agents')}
            </Checkbox>
          )}
          {divider}
          <div className="grui pt-3 pb-5">
            {filteredList.map(({ id, name, avatar_url: avatarUrl }) => (
              <MenuWithSearch.Item itemKey={id} key={id}>
                <Checkbox
                  css={styles.checkbox}
                  truncate
                  id={id}
                  onChange={handleChange}
                  checked={selectedIds.some(itemId => itemId === id)}
                >
                  <div className="grui truncate">
                    <Avatar
                      avatar={avatarUrl}
                      userName={name}
                      className="grui mr-5"
                    />
                    {name}
                  </div>
                </Checkbox>
              </MenuWithSearch.Item>
            ))}
          </div>
        </MenuWithSearch>
      )
    },
    [
      filteredList,
      indeterminate,
      allChecked,
      selectedIds,
      handleChange,
      handleCheckAllChange,
      handleScroll,
      search,
    ]
  )
  const header = useMemo(
    () => (
      <MenuWithSearch.Search
        placeholder={`Search ${app.t('agents')}…`}
        onChange={handleChangeSearch}
        value={search}
        ref={searchRef}
        shouldFocus={menuVisible}
      />
    ),
    [handleChangeSearch, search, menuVisible]
  )

  const footer = useMemo(
    () => (
      <Footer
        onSelect={handleSelect}
        onCancel={handleCancel}
        saveBtnTitle={`Select ${app.t('agents')}`}
      />
    ),
    [handleSelect, handleCancel]
  )

  const selectedFirstNames = useMemo(
    () => {
      if (agents.length && agents.length === value.length) {
        return `All ${app.t('agents')}`
      }
      return value
        .map(id => agents.find(agent => agent.id === id)?.first_name)
        .join(', ')
    },
    [value, agents]
  )

  useEffect(
    () => {
      if (!search?.trim()) {
        setFilteredList(agents)
      }
    },
    [search, agents]
  )

  useEffect(
    () => {
      if (!menuVisible) {
        onSelectedIdsChange(value)
        setSearch('')
      }
    },
    // Including onSelectedIdsChange causes circular renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [menuVisible, value]
  )

  return (
    <Dropdown
      overlay={menu}
      header={header}
      footer={footer}
      css={[
        text.styles.textSm,
        styles.dropdownContainer,
        shouldShowHeaderShadow && menuWithSearchStyles.showHeaderShadow,
      ]}
      visible={menuVisible}
      onVisibleChange={handleMenuVisibleChange}
      emptyHint=""
      autoHeight
    >
      <Dropdown.Button css={styles.dropdownBtn} size="small">
        {value.length ? selectedFirstNames : `Select ${app.t('agents')}`}
      </Dropdown.Button>
    </Dropdown>
  )
}

export default TeammatesDropdown
